<script setup lang="ts">
import { computed } from 'vue'

const props = withDefaults(
  defineProps<{
    /** Applies the default inner padding; set false for flush cards whose media bleeds to the edge. */
    padding?: boolean
    /** Drops the inner padding one step on the scale. Ignored when `padding` is false. */
    dense?: boolean
    /** Renders the card as a `<button>` with hover/focus affordances; bind `@click` directly. */
    interactive?: boolean
    /** The element the card renders: `button` when `interactive`, else `div` by default · `label`, an interactive card that wraps its own control, such as a checkbox, and takes its focus ring from it · `span`, a card inside phrasing content, such as a part of a larger button. */
    as?: 'div' | 'button' | 'label' | 'span'
    /** Paints the border as the active accent. Ignored when not `interactive`. */
    selected?: boolean
    /** Force the hover edge, for specimens without a real pointer. Ignored unless `interactive` or `grouped`. */
    isHovered?: boolean
    /** Paints the hover edge while the pointer is over the nearest `group/card` ancestor: the card is one part of a larger click target. */
    grouped?: boolean
    /** What the pointer paints on an `interactive` card: `edge` (default), the accent border · `none`, nothing, for a card that draws its own hover cue. */
    hover?: 'edge' | 'none'
    /** A dashed hairline over no fill in place of the glass: a slot that is planned but nothing fills yet. */
    hollow?: boolean
    /** The fill: `glass` (default), a stand-alone object on the page · `wash`, a flat grey option inside a dialog or a sheet that turns blue while `selected`. */
    surface?: 'glass' | 'wash'
  }>(),
  {
    padding: true,
    dense: false,
    interactive: false,
    as: undefined,
    selected: false,
    isHovered: false,
    hover: 'edge',
    hollow: false,
    grouped: false,
    surface: 'glass',
  },
)

const hoverEdge = computed(() => props.hover === 'edge')
const tag = computed(() => props.as ?? (props.interactive ? 'button' : 'div'))

const borderColorClass = computed(() => {
  if (props.interactive && (props.selected || (props.isHovered && hoverEdge.value))) {
    return 'border-accent-blue'
  }
  if (props.grouped && props.isHovered) return 'border-accent-blue'
  return props.hollow ? 'border-border' : 'border-transparent'
})

const fillClass = computed(() => {
  if (props.hollow) return 'border-dashed bg-transparent'
  if (props.surface === 'glass') return 'glass-card'
  return props.interactive && props.selected ? 'bg-accent-blue-wash' : 'bg-bg-wash'
})
</script>

<template>
  <component
    :is="tag"
    :type="tag === 'button' ? 'button' : undefined"
    :class="[
      'flex flex-col rounded-4 border',
      fillClass,
      borderColorClass,
      grouped && [
        'transition-colors duration-fast ease-soft motion-reduce:transition-none',
        'group-hover/card:border-accent-blue',
      ],
      padding && (dense ? 'p-8' : 'p-12'),
      interactive && [
        'w-full cursor-pointer text-left',
        'outline-none transition-colors duration-fast ease-soft motion-reduce:transition-none',
        hoverEdge && 'hover:border-accent-blue',
        tag === 'label'
          ? 'has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-solid has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-accent-blue'
          : 'focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-offset-2 focus-visible:outline-accent-blue',
      ],
    ]"
  >
    <slot />
  </component>
</template>
